import React, { useState, useMemo } from "react";
import { ClientProjectItem } from "../types/clients-module-types";
import {
  dateFormatEntryEdit,
  currencyFormat,
} from "../../SharedModule/utils/formatters";
import {
  changeProjectsOrder,
  IsSeeProjectActives,
  SelectProjectsOrderCriteria,
  SelectSearchTextProject,
  setSearchTextProject,
  setSeeProjectActives,
} from "../redux/reducers/clientWork.reducer";
import { useDispatch, useSelector } from "react-redux";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";

export const AcordionProjectsTable = React.memo(
  ({ clientProjects }: { clientProjects: Array<ClientProjectItem> | null }) => {
    const dispatch = useDispatch();

    const sortBy = useSelector(SelectProjectsOrderCriteria);
    const selectSearchTextProject = useSelector(SelectSearchTextProject);
    const isSeeProjectActives = useSelector(IsSeeProjectActives);

    const [seeProjectsActive, setSeeProjectsActive] =
      useState(isSeeProjectActives);
    const [projectsSearchText, setProjectsSearchText] = useState(
      selectSearchTextProject
    );

    let clientProjectsToShow: Array<ClientProjectItem> = useMemo(
      () =>
        (clientProjects ?? []).filter((elem) => {
          const lowerSearch = projectsSearchText.toLocaleLowerCase();
          return (
            elem.id.toString().toLocaleLowerCase().includes(lowerSearch) ||
            elem.projectName.toLocaleLowerCase().includes(lowerSearch)
          );
        }),
      [clientProjects, projectsSearchText]
    );

    const updateSeeProjectActives = (value: boolean) => {
      dispatch(setSeeProjectActives(value));
      setSeeProjectsActive(value);
    };

    const search = (value: string) => {
      setProjectsSearchText(value);
    };

    const blurHandler = () => {
      dispatch(setSearchTextProject(projectsSearchText));
    };

    const goToProjectDetails = (id: number) => {
      const baseUrl = process.env.REACT_APP_ASUITE_PROJECT_URI ?? "";
      const newWindow = window.open(baseUrl + id + "&pag=L", "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    };

    return (
      <>
        {!clientProjects ||
          (clientProjects.length === 0 && (
            <NoContent
              title="No Projects for this Client"
              image={image}
              style={{ margin: "0 auto" }}
            />
          ))}

        {clientProjects && clientProjects.length > 0 && (
          <>
            <div className="d-flex mb-3 mx-2 justify-content-between">
              <div className="d-flex flex-column col-sm-9">
                <label htmlFor="proj-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="proj-search"
                  type="text"
                  onBlur={blurHandler}
                  className="form-control form-control-sm common-input"
                  placeholder="Search by Project Name or ID"
                  value={projectsSearchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>

              <fieldset className="d-flex flex-column">
                <div className="col-form-label">Project Status</div>
                <div className="btn-group w-100">
                  <button
                    type="button"
                    onClick={() => updateSeeProjectActives(true)}
                    className={`btn btn-group-custom ${
                      !seeProjectsActive ? "btn-light" : "btn-active"
                    }`}
                    disabled={seeProjectsActive}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSeeProjectActives(false)}
                    className={`btn btn-group-custom ${
                      !seeProjectsActive ? "btn-inactive" : "btn-light"
                    }`}
                    disabled={!seeProjectsActive}
                  >
                    Inactive
                  </button>
                </div>
              </fieldset>
            </div>

            {clientProjectsToShow?.filter((elem) =>
              seeProjectsActive ? elem.active : !elem.active
            ).length === 0 && (
              <NoContent
                title="No Results"
                text="Edit your filters to find the work items you are looking for."
                image={image}
                style={{ margin: "0 auto" }}
              />
            )}

            {clientProjectsToShow?.filter((elem) =>
              seeProjectsActive ? elem.active : !elem.active
            ).length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-2"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-10 pointer ${
                        sortBy.orderBy === "id"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="id"
                      key="id"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "id",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      ID
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-25 pointer ${
                        sortBy.orderBy === "projectBillingName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="projectBillingName"
                      key="projectBillingName"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "projectBillingName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Project Name
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "dueDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="dueDate"
                      key="dueDate"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "dueDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      {seeProjectsActive ? "Due Date" : "Date Complete"}
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "costCenterNumber"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="costCenterNumber"
                      key="costCenterNumber"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "costCenterNumber",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Cost Center
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "projectManager"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="projectManager"
                      key="projectManager"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "projectManager",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Project Manager
                    </th>
                    <th
                      className={`border-dark border-top fw-600 pe-4 text-end column-10 pointer ${
                        sortBy.orderBy === "fixedPrice"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="fixedPrice"
                      key="fixedPrice"
                      onClick={() =>
                        dispatch(
                          changeProjectsOrder({
                            orderBy: "fixedPrice",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Budget
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientProjectsToShow
                    ?.filter((elem) =>
                      seeProjectsActive ? elem.active : !elem.active
                    )
                    .map((item: ClientProjectItem) => {
                      const costCenterActiveSuffix = item.costCenterActive
                        ? ""
                        : "(X)";
                      const projectManagerActiveSuffix =
                        item.projectManagerActive ? "" : "(X)";
                      return (
                        <tr
                          key={item.id}
                          className="client-contant-row-no-clickable"
                        >
                          <td className="ps-4 fw-500">
                            <span
                              className="link-text link-text-underline"
                              role="link"
                              tabIndex={0}
                              onClick={() => goToProjectDetails(item.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  goToProjectDetails(item.id);
                                }
                              }}
                            >
                              {item.id}
                            </span>
                          </td>
                          <td className="ps-4 fw-500">{item.projectName}</td>
                          <td className="ps-4 fw-500">
                            {item.dueDate
                              ? dateFormatEntryEdit(item.dueDate)
                              : ""}
                          </td>
                          <td className="ps-4 fw-500">
                            {item.costCenterName && item.costCenterNumber
                              ? `${item.costCenterNumber} - ${item.costCenterName} ${costCenterActiveSuffix}`
                              : ""}
                          </td>
                          <td className="ps-4 fw-500">
                            {item.projectManager
                              ? `${item.projectManager} ${projectManagerActiveSuffix}`
                              : ""}
                          </td>
                          <td className="pe-4 text-end fw-500">
                            {currencyFormat(
                              item.projectBillingId === 2
                                ? item.estimatedPrice
                                : item.fixedPrice
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
      </>
    );
  }
);
